import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { ChartServices } from "../../../core/services/ChartServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { SongFields, SongListParams } from "../Songs/SongDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./Charts.css";
import SongFilters from "../Songs/SongFilters";
import {
  displayDayDateField,
  isEmptyVal,
  isNotEmpty,
  displayField,
  genreTitle,
} from "../../../utils/helpers";
import DescriptionCell from "../../components/Elements/DescriptionCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import useDecryptedParams from "../../../navigation/useDecryptParams";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { SongServices } from "../../../core/services/SongServices";
import SingerCell from "../../components/Elements/SingerCell";
import SongInfoCell from "../../components/Elements/SongInfoCell";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import AddSongsToChartBulkAction from "./AddSongsToChartBulkAction";
import { setSearchString } from "../../../redux/user";
import AudioPlayer from "../../components/Elements/AudioPlayer";
import { useMediaContext } from "../../../context/MediaContext";

function AddSongsToChart() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<SongFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [licenseIds, setLicenseIds] = useState<string>("");
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const params: any = useParams();
  let [decryptedParams] = useDecryptedParams(params);
  const [chartLoader, setChartLoader] = useState(false);
  const [chartData, setChartData] = useState<any>({});
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();

  const initSearchFormData = {
    language: "",
    language_id: "",
    mood: "",
    mood_id: "",
    name: "",
    singer: "",
    singer_id: "",
    singer_gender: "",
    singer_combination: "",
    actor: "",
    actor_id: "",
    lyricist: "",
    lyricist_id: "",
    composer: "",
    composer_id: "",
    label: "",
    label_id: "",
    genre: "",
    genre_id: "",
    sub_genre: "",
    sub_genre_id: "",
    license: "",
    license_id: "",
    song_type: "",
    media_type: "",
    sourced_from: "",
    media_category: "",
    tempo: "",
    tempo_id: "",
    era: "",
    era_id: "",
    album: "",
    festive_season: "",
    festive_season_id: "",
    release_year: "",
    rating_chart: "",
    rating_chart_id: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  function getChartId() {
    return decryptedParams.id ? decryptedParams.id.toString() : null;
  }
  async function getChartDetail(id: number) {
    try {
      setChartLoader(true);
      const res = await ChartServices.viewChart(id);
      const respData = get("data.data", res);
      setChartData({ ...respData });
      setLicenseIds(respData.license_id);
      getSongsForChart(
        getSearchParams({
          license_id: respData.license_id,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      setChartLoader(false);
    } catch (error) {
      setChartLoader(false);
    }
  }
  async function getSongsForChart(params: SongListParams) {
    try {
      setLoading(true);
      const res = await SongServices.getSongs(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      navigate(ContentRoutes.CHARTS);
    }
  }

  async function bulkAddAction() {
    try {
      let params = { song_ids: checkedKeys.join() };
      let res = await ChartServices.addSongsToChart(chartData.id, params);
      UtilityServices.showToaster({
        message: res.data.message,
      });
      getSongsForChart(
        getSearchParams({
          smart_search: smartSearchInput,
        })
      );
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      getSongsForChart(
        getSearchParams({
          smart_search: smartSearchInput,
        })
      );
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function init() {
    try {
      getChartId() && getChartDetail(getChartId());
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, [decryptedParams.id]);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: SongListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    if (getChartId()) {
      params.exclude_songs_from_chart = getChartId();
    }

    if (licenseIds !== "") {
      params.license_id = licenseIds;
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getSongsForChart(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getSongsForChart(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getSongsForChart(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSongsForChart(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterSongs = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getSongsForChart(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getSongsForChart(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getSongsForChart(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };
  return (
    <div>
      {chartLoader && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content={t("LOADING")}
          center
          backdrop={true}
        />
      )}
      {getChartId() > 0 ? (
        <div className="followups">
          <Container className="container">
            <Header className="header mb-2">
              <FlexboxGrid justify="space-between">
                <FlexboxGrid.Item colspan={16}>
                  <h3>
                    <b>
                      {" "}
                      {t("CHART")} {t("NAME")}
                    </b>{" "}
                    {chartData?.chart_title}
                  </h3>
                  <h4>
                    <b> {t("LICENSE")}</b> {chartData?.license?.license_name}
                    <label>
                      {" ("}
                      <b>
                        {t("CHART")} {t("TYPE")}{" "}
                      </b>{" "}
                      : {chartData?.chart_type}{" "}
                      {chartData?.chart_type == "event"
                        ? t(",") + " " + chartData?.event?.event_name
                        : null}{" "}
                      {" )"}
                    </label>
                  </h4>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Header>
            <Content>
              {licenseIds !== "" && (
                <SongFilters
                  onSubmit={submitSearch}
                  onClear={clearSearch}
                  onlyLicenses={licenseIds}
                  hideFilters={["license"]}
                />
              )}

              <div className="row">
                <AddSongsToChartBulkAction
                  chartData={chartData}
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onAddSongs={bulkAddAction}
                  onDeleteSongs={bulkDeleteAction}
                />
                <div className="col-md-4">
                  <InputGroup>
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity: t("TITLE").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterSongs(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterSongs(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                  </InputGroup>
                </div>
              </div>

              <div
                className={
                  total > 0 ? "usr-tbl-border mt-2" : "no-record-table mt-2"
                }
              >
                <Table
                  height={400}
                  fillHeight={true}
                  data={data}
                  loading={loading}
                  onSortColumn={onSortChange}
                  sortColumn={sort_by}
                  bordered={true}
                  cellBordered
                  sortType={sort_order}
                  hover={true}
                  renderEmpty={() => (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                      <h4>
                        {t("NO_ENTITY_FOUND_FOR", {
                          entity: t("SONG"),
                          for: chartData?.chart_title,
                        })}
                      </h4>
                    </div>
                  )}
                >
                  <Column width={40} align="center" fixed>
                    <CustomHeaderCell>
                      <div>
                        <Checkbox
                          inline
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={handleCheckAll}
                        />
                      </div>
                    </CustomHeaderCell>
                    <CheckCell
                      dataKey="id"
                      checkedKeys={checkedKeys}
                      onChange={handleCheck}
                    />
                  </Column>
                  <Column width={60} align="center" fixed>
                    <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                    <MediaPlayerFormatter
                      rowData={(rowData: any) => rowData}
                      mediaUrlKey={"media_url"}
                      mediaImageKey={"media_image"}
                    />
                  </Column>
                  <Column width={200} sortable resizable flexGrow={1}>
                    <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                    <CustomCell dataKey="title">
                      {(rowData: any) => (
                        <span title={`${rowData.title}`}>
                          {displayField(rowData.title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={160}>
                    <CustomHeaderCell>{t("SINGER")}</CustomHeaderCell>
                    <SingerCell rowData={(rowData: any) => rowData} />
                  </Column>
                  <Column width={160} sortable resizable>
                    <CustomHeaderCell>{t("GENRE")}</CustomHeaderCell>
                    <CustomCell dataKey="genre_title">
                      {(rowData: any) => (
                        <span title={genreTitle(rowData)}>
                          {displayField(rowData.genre_title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={140} resizable>
                    <CustomHeaderCell>{t("LANGUAGE")}</CustomHeaderCell>
                    <CustomCell dataKey="language_title">
                      {(rowData: any) => (
                        <span title={`${rowData.language_title}`}>
                          {displayField(rowData.language_title)}
                        </span>
                      )}
                    </CustomCell>
                  </Column>
                  <Column width={80}>
                    <CustomHeaderCell>{t("MORE")}</CustomHeaderCell>
                    <SongInfoCell
                      rowData={(rowData: any) => rowData}
                      showData={[
                        "era",
                        "mood",
                        "license",
                        "lyricist",
                        "composer",
                        "tempo",
                      ]}
                    />
                  </Column>
                </Table>
              </div>
            </Content>
            <Footer className="footer">
              <Pagination
                prev
                next
                first
                last
                ellipsis
                boundaryLinks
                maxButtons={5}
                size="xs"
                layout={["total", "-", "limit", "|", "pager", "skip"]}
                total={total}
                limitOptions={appConstObj.limit_options}
                limit={rows}
                activePage={page}
                onChangePage={onPageChange}
                onChangeLimit={handleChangeRows}
              />
            </Footer>
            <AudioPlayer />
            <ToastContainer />
          </Container>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export default AddSongsToChart;
